import { useEffect } from "preact/hooks";
import type { RoutableProps } from "preact-router";
import { route } from "preact-router";
import { Navbar } from "../../components/layout/Navbar";

/* ── Plan data ─────────────────────────────────────────────── */

interface PlanFeature {
  label: string;
  included: boolean;
}

interface Plan {
  code: string;
  name: string;
  price: string;
  period: string;
  description: string;
  features: PlanFeature[];
  cta: string;
  ctaHref: string;
  variant: "primary" | "secondary";
  highlighted?: boolean;
}

const plans: Plan[] = [
  {
    code: "free",
    name: "Free",
    price: "\u20AC0",
    period: "/month",
    description: "For individuals getting started.",
    features: [
      { label: "25 hash lookups / day", included: true },
      { label: "Public community reports", included: true },
      { label: "7-day private lookup history", included: true },
      { label: "API access", included: false },
      { label: "Saved reports", included: false },
    ],
    cta: "Start free",
    ctaHref: "/login",
    variant: "secondary",
  },
  {
    code: "hobbyist",
    name: "Hobbyist",
    price: "\u20AC1.99",
    period: "/month",
    description: "For enthusiasts and independent researchers.",
    features: [
      { label: "100 hash lookups / day", included: true },
      { label: "Private report history", included: true },
      { label: "30-day retention", included: true },
      { label: "Basic exports", included: true },
      { label: "API access", included: false },
      { label: "Saved reports", included: false },
    ],
    cta: "Start Hobbyist",
    ctaHref: "/login",
    variant: "primary",
    highlighted: true,
  },
  {
    code: "analyst",
    name: "Analyst",
    price: "\u20AC19.99",
    period: "/month",
    description: "For professional analysts. Extra usage billed separately.",
    features: [
      { label: "500 hash lookups / day", included: true },
      { label: "Extra usage at cost", included: true },
      { label: "Full private report history", included: true },
      { label: "180-day retention", included: true },
      { label: "API keys", included: true },
      { label: "Saved reports", included: true },
      { label: "Richer exports", included: true },
      { label: "Priority access", included: true },
    ],
    cta: "Start Analyst",
    ctaHref: "/login",
    variant: "primary",
  },
];

/* ── Component ─────────────────────────────────────────────── */

export function PricingPage(_props: RoutableProps) {
  useEffect(() => {
    document.title = "Sage | Pricing";
  }, []);

  function handleCta(e: MouseEvent, plan: Plan) {
    e.preventDefault();
    if (plan.ctaHref.startsWith("mailto:")) {
      window.location.href = plan.ctaHref;
    } else {
      route(plan.ctaHref);
    }
  }

  return (
    <div class="app-shell">
      <Navbar />

      <main class="pricing-main">
        {/* Header */}
        <div class="pricing-header">
          <h1 class="pricing-title">Simple, transparent pricing</h1>
          <p class="pricing-desc">
            Choose the plan that fits your threat intelligence workflow.
            All plans include community hash intelligence. Upgrade for private
            lookups, API access, and team collaboration.
          </p>
        </div>

        {/* Cards */}
        <div class="pricing-grid" role="list">
          {plans.map((plan) => (
            <div
              key={plan.code}
              class={`pricing-card${plan.highlighted ? " pricing-card--highlighted" : ""}`}
              role="listitem"
            >
              <div class="pricing-card__head">
                <h2 class="pricing-card__name">{plan.name}</h2>
                <p class="pricing-card__desc">{plan.description}</p>
              </div>

              <div class="pricing-card__price">
                <span class="pricing-card__amount">{plan.price}</span>
                {plan.period && (
                  <span class="pricing-card__period">{plan.period}</span>
                )}
              </div>

              <ul class="pricing-card__features">
                {plan.features.map((feat) => (
                  <li
                    key={feat.label}
                    class={`pricing-card__feature${feat.included ? "" : " pricing-card__feature--excluded"}`}
                  >
                    <span class="pricing-card__feature-icon" aria-hidden="true">
                      {feat.included ? "\u2713" : "\u2013"}
                    </span>
                    {feat.label}
                  </li>
                ))}
              </ul>

              <a
                class={`pricing-card__cta${plan.variant === "primary" ? " pricing-card__cta--primary" : ""}`}
                href={plan.ctaHref}
                onClick={(e) => handleCta(e, plan)}
              >
                {plan.cta}
              </a>
            </div>
          ))}
        </div>

      </main>
    </div>
  );
}
